import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, Hash, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { DataPagination } from '@/components/DataPagination';
import { SOURCE_CLASSES, AI_HANDOFF_REASON_LABELS, listAiUnanswered } from '@/lib/support';

// Every ticket BMC Assistant handed off to a human, newest first — not just
// the no_answer ones with a rephrased FAQ-gap question. A row's `question` is
// only set when the AI generated FAQ-gap text (still just no_answer); every
// other reason shows the reason itself instead, so the team sees everything
// the AI couldn't close out, not only what's missing from the FAQ. Read-only:
// rows are written by the worker.

const PORTAL_LABELS: Record<string, string> = { creator: 'Creator app', brand: 'Brand', coach: 'BMC Coach' };

export function AiUnansweredDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, error } = useQuery({
    queryKey: ['support-ai-unanswered', currentPage, pageSize],
    queryFn: () => listAiUnanswered(currentPage - 1, pageSize),
    enabled: open,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" /> Handed off by BMC Assistant
          </DialogTitle>
          <DialogDescription>
            Every ticket the AI couldn't close out itself, newest first. "Not covered by the FAQ" rows show the
            customer's question rephrased generically — add the answer to the knowledge file so the assistant
            can handle it next time. Everything else is a reminder of why it still needed a human.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-2 overflow-y-auto">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
          ) : error ? (
            <p className="py-8 text-center text-sm text-destructive">Could not load tickets.</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nothing yet — the AI hasn't handed off any tickets.</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="flex items-start gap-3 rounded-lg border p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    {r.question ?? <span className="italic text-muted-foreground">{AI_HANDOFF_REASON_LABELS[r.reason]}</span>}
                  </p>
                  {r.ticket_number != null && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs">
                      <span className="inline-flex items-center gap-0.5 font-mono font-semibold tabular-nums text-muted-foreground">
                        <Hash className="h-3 w-3" />{r.ticket_number}
                      </span>
                      <span className="truncate font-medium" title={r.ticket_subject ?? undefined}>
                        {r.ticket_subject || 'No subject'}
                      </span>
                    </p>
                  )}
                  {r.first_message && (
                    <div className="mt-1.5 rounded border-l-2 border-muted-foreground/40 bg-muted/40 px-2 py-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">First message</p>
                      <p className="line-clamp-3 whitespace-pre-wrap break-words text-xs">{r.first_message}</p>
                    </div>
                  )}
                  {r.error_detail && (
                    <p className="mt-1 break-words rounded bg-destructive/5 px-2 py-1 font-mono text-xs text-destructive">
                      {r.error_detail}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className={`rounded-full border px-1.5 py-0 text-[10px] font-semibold ${SOURCE_CLASSES[r.portal === 'brand' ? 'brand' : 'creator']}`}>
                      {PORTAL_LABELS[r.portal] ?? r.portal}
                    </span>
                    {r.question && (
                      <span className="rounded-full border px-1.5 py-0 text-[10px] font-semibold text-muted-foreground">
                        {AI_HANDOFF_REASON_LABELS[r.reason]}
                      </span>
                    )}
                    <span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
                {r.ticket_id && (
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Open the ticket"
                    onClick={() => { onOpenChange(false); navigate(`/ticket-chat/${r.ticket_id}`); }}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {total > 0 && (
          <DataPagination
            currentPage={currentPage}
            totalPages={Math.ceil(total / pageSize)}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => { setCurrentPage(1); setPageSize(newSize); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
